import React, { useState, useEffect } from "react"
import axios from "axios"

import { useLocale } from "../../../context/LocaleContext"
import useDictionary from "../../../hooks/useDictionary"

type ApiModel = {
  name: {
    common: string
  }
  cca2: string
}

type CountryOption = {
  value: string
  label: string
  countryCode: string
}

interface Props {
  formFunctions: any
  name?: string
  label: string
  placeholder?: string
  value?: string
  required?: boolean
  hideLabel?: boolean
  instructions?: string
  pageContext: any
}

const defaultProps = {}

const emptyCountryList = [
  {
    value: "Australia",
    label: "Australia",
    countryCode: "AU",
  },
  {
    value: "New Zealand",
    label: "New Zealand",
    countryCode: "NZ",
  },
]

const CountrySelect: React.FC<Props> = props => {
  // react-hook-form
  const register = props.formFunctions.register
  const errors = props.formFunctions.errors

  // state
  const [countries, setCountries] = useState(emptyCountryList)

  // hooks
  const { localeDir } = useLocale().localeData

  // load country list from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get("https://restcountries.com/v3.1/all")
        const data = response.data
          .map((x: ApiModel) => {
            return {
              value: x.name.common,
              label: x.name.common,
              countryCode: x.cca2,
            }
          })
          .sort((a: CountryOption, b: CountryOption) =>
            a.label > b.label ? 1 : -1
          )
        setCountries(data)
      } catch (e) {
        setCountries(emptyCountryList)
      }
    }
    fetchCountries()
  }, [])

  // set default country as the site locale
  useEffect(() => {
    const defaultCountry = countries.find(
      option => option.countryCode === localeDir?.toUpperCase()
    )
    props.formFunctions.setValue("Country", defaultCountry?.value, {
      shouldValidate: true,
    })
  }, [countries, localeDir])

  // field props
  const input_name = props.name || props.label
  const input_id = input_name.split(" ").join("_")

  return (
    <div className="form__field">
      <label htmlFor={input_id}>
        {props.hideLabel ? (
          <span className="show-for-sr">{props.label}</span>
        ) : (
          <>
            {props.label}
            {props.required ? " *" : ""}
          </>
        )}
      </label>

      <div className="field__input">
        <select
          id={input_id}
          className={errors && errors[input_name] && "error"}
          {...register(input_name, {
            required: props.required
              ? `${props.label} ${useDictionary(
                  "is required",
                  props.pageContext.locale
                )}`
              : false,
          })}
        >
          <option value="">{props.placeholder || ""}</option>
          {countries.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {props.instructions && (
        <div className="field__help">{props.instructions}</div>
      )}

      {errors.hasOwnProperty(input_name) && (
        <div className="field__error">{errors[input_name].message}</div>
      )}
    </div>
  )
}

CountrySelect.defaultProps = defaultProps

export default CountrySelect
